// Klaviyo Signup Enhancements
(function () {
  'use strict';

  // Configuration from theme settings (injected by Liquid)
  const config = {
    formId: window.klaviyoFormId || 'YOUR_FORM_ID',
    dismissalDays: window.klaviyoDismissalDays || 4,
    fallbackUrl: '/pages/newsletter-signup'
  };

  // Technical constants
  const IDLE_DELAY = 4000; // 4 seconds for idle detection

  // State
  let idleTimer = null;
  let bannerIsVisible = false;
  let isScrolling = false;
  let idleResetTimeout = null;
  let storageInitialized = false; // Track if we've synced storage
  let isFormOpen = false; // DEF-001: Track if Klaviyo form is currently open
  let formOpenTimeout = null; // DEF-001: Fallback timeout in case form fails to open

  // Storage keys we need to sync
  const STORAGE_KEYS = ['klaviyoCTADismissal', 'bannerHiddenSession'];

  // Check if localStorage is available
  function isLocalStorageAvailable() {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, 'test');
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  // Sync localStorage to sessionStorage on page load
  function syncStorageOnLoad() {
    if (storageInitialized) return;

    // Copy all relevant values from localStorage to sessionStorage
    if (isLocalStorageAvailable()) {
      STORAGE_KEYS.forEach(key => {
        try {
          const value = localStorage.getItem(key);
          if (value !== null) {
            sessionStorage.setItem(key, value);
          }
        } catch (e) {
          // Silent fail - continue with other keys
        }
      });
    }

    storageInitialized = true;
  }

  // Storage wrapper functions - sessionStorage as primary, localStorage for persistence
  function getStorageItem(key) {
    try {
      // Always read from sessionStorage (primary source during session)
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function setStorageItem(key, value) {
    try {
      // Always write to sessionStorage first (primary)
      sessionStorage.setItem(key, value);

      // Try to persist to localStorage (best effort)
      if (isLocalStorageAvailable()) {
        try {
          localStorage.setItem(key, value);
        } catch (e) {
          // Silent fail - sessionStorage has the value
        }
      }
    } catch (e) {
      // If even sessionStorage fails, we can't store
      // Storage completely unavailable - fail silently
    }
  }

  function removeStorageItem(key) {
    try {
      // Remove from both storages
      sessionStorage.removeItem(key);

      if (isLocalStorageAvailable()) {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          // Silent fail
        }
      }
    } catch (e) {
      // Silent fail
    }
  }

  // Universal CTA dismissal tracking (works for both banner and teaser)
  function setCTADismissal(reason = 'manual_close') {
    const dismissalData = {
      dismissed: true,
      timestamp: new Date().getTime(),
      reason: reason
    };
    setStorageItem('klaviyoCTADismissal', JSON.stringify(dismissalData));
  }

  // Check if user has dismissed any CTA (banner or teaser)
  function hasCTABeenDismissed() {
    const dismissalData = getStorageItem('klaviyoCTADismissal');
    if (!dismissalData) return false;

    try {
      const data = JSON.parse(dismissalData);
      return data.dismissed === true;
    } catch (e) {
      return false;
    }
  }

  // Check if CTA dismissal has expired (4 days)
  function isCTADismissalExpired() {
    const dismissalData = getStorageItem('klaviyoCTADismissal');
    if (!dismissalData) return true;

    try {
      const data = JSON.parse(dismissalData);

      // Never expire signup dismissals - user already converted
      if (data.reason === 'signup') {
        return false;
      }

      // Check expiry only for manual dismissals
      const daysSinceDismissal = (new Date().getTime() - data.timestamp) / (1000 * 60 * 60 * 24);

      if (daysSinceDismissal >= config.dismissalDays) {
        // DON'T delete the dismissal - we need it to show header CTA
        // Just clear session storage to allow teaser/banner to reappear
        removeStorageItem('bannerHiddenSession');
        return true;
      }
      return false;
    } catch (e) {
      return true;
    }
  }

  // Universal check if any CTA (banner or teaser) should be shown
  function shouldShowCTA() {
    // Check session dismissal
    if (getStorageItem('bannerHiddenSession')) {
      return false;
    }

    // Check if dismissal hasn't expired (still within 4 days)
    if (hasCTABeenDismissed() && !isCTADismissalExpired()) {
      return false;
    }

    return true;
  }

  // Check if banner should be shown (mobile-specific wrapper)
  function shouldShowBanner() {
    return shouldShowCTA();
  }

  // Check if banner should hide when near footer
  function shouldHideNearFooter() {
    // Look specifically for footer-bottom div
    const footerBottom = document.querySelector('.footer-bottom');
    const banner = document.getElementById('mobile-sticky-banner');
    if (!footerBottom || !banner) return false;

    // Get positions
    const footerBottomRect = footerBottom.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // Hide banner when footer-bottom enters viewport (similar to how sticky header hides when main header is visible)
    // Check if footer-bottom top is visible in viewport
    return footerBottomRect.top < windowHeight;
  }

  // ========== SIMPLIFIED CTA FUNCTIONS ==========

  // Show floating CTAs (teaser or banner based on viewport)
  function showFloatingCTAs() {
    const teaser = document.getElementById('desktop-floating-teaser');
    const banner = document.getElementById('mobile-sticky-banner');

    // Desktop: show teaser
    if (teaser && window.innerWidth >= 1024) {
      teaser.classList.remove('hidden');
      teaser.classList.add('is-visible');
    }

    // Mobile: show banner
    if (banner && window.innerWidth < 1024) {
      banner.classList.remove('hidden');
      banner.classList.add('is-visible');
      bannerIsVisible = true;

      // Sync with unified scroll manager if available
      if (window.unifiedScrollManager) {
        window.unifiedScrollManager.updateComponentState('mobileBanner', true);
      }
    }

    // Always hide header CTA when showing floating
    hideHeaderCTA();
  }

  // Hide floating CTAs
  function hideFloatingCTAs() {
    const teaser = document.getElementById('desktop-floating-teaser');
    const banner = document.getElementById('mobile-sticky-banner');

    if (teaser) {
      teaser.classList.add('hidden');
      teaser.classList.remove('is-visible');
    }

    if (banner) {
      banner.classList.add('hidden');
      banner.classList.remove('is-visible');
      bannerIsVisible = false;

      // Sync with unified scroll manager if available and initialized
      if (window.unifiedScrollManager && window.unifiedScrollManager.componentStates) {
        window.unifiedScrollManager.updateComponentState('mobileBanner', false);
      }
    }
  }

  // Show header CTA
  function showHeaderCTA() {
    const headerCta = document.getElementById('klaviyo-header-cta');
    const stickyHeaderCta = document.getElementById('klaviyo-header-cta-sticky');

    [headerCta, stickyHeaderCta].forEach(cta => {
      if (cta) {
        // First, make the CTA take up space but remain invisible
        cta.style.visibility = 'hidden';
        cta.classList.add('is-visible'); // This removes display: none

        // Force immediate resize so logo adjusts to new layout
        window.dispatchEvent(new Event('resize'));

        // Then make it visible after layout has adjusted
        setTimeout(() => {
          cta.style.visibility = 'visible';
        }, 1);
      }
    });
  }

  // Hide header CTA
  function hideHeaderCTA() {
    const headerCta = document.getElementById('klaviyo-header-cta');
    const stickyHeaderCta = document.getElementById('klaviyo-header-cta-sticky');

    [headerCta, stickyHeaderCta].forEach(cta => {
      if (cta) {
        cta.classList.remove('is-visible');
      }
    });
  }

  // Hide all CTAs (for signup)
  function hideAllCTAs() {
    hideFloatingCTAs();
    hideHeaderCTA();
  }

  // Handle manual dismissal of floating CTAs
  function handleFloatingDismissal() {
    hideFloatingCTAs();
    showHeaderCTA();
    setCTADismissal('manual_close');
    setStorageItem('bannerHiddenSession', 'true');
  }

  // Handle email signup
  function handleSignup() {
    hideAllCTAs();
    setCTADismissal('signup');
  }

  // ========== END SIMPLIFIED FUNCTIONS ==========

  // Mobile-specific functions for scroll/idle behavior
  function showMobileBanner() {
    const banner = document.getElementById('mobile-sticky-banner');
    if (!banner || bannerIsVisible || shouldHideNearFooter()) return;

    // Check if we should show based on dismissal state
    if (!shouldShowCTA()) return;

    // Show floating CTAs
    showFloatingCTAs();
  }

  function hideMobileBanner(reason = 'passive') {
    const banner = document.getElementById('mobile-sticky-banner');
    if (!banner || !bannerIsVisible) return;

    if (reason === 'manual_close') {
      handleFloatingDismissal();
    } else {
      // Just hide temporarily for scroll
      banner.classList.add('hidden');
      banner.classList.remove('is-visible');
      bannerIsVisible = false;
    }
  }

  // Handle scroll events from unified scroll manager
  function handleScroll(event) {
    const scrollState = event.detail;

    // Check if near footer first
    if (shouldHideNearFooter() && bannerIsVisible) {
      hideMobileBanner('near-footer');
      clearTimeout(idleTimer);
      return;
    }

    // Use unified state for show/hide decisions
    if (scrollState.canToggle && scrollState.isPastThreshold) {
      if (scrollState.direction === 'down' && bannerIsVisible) {
        hideMobileBanner('scroll');
        // Clear and restart idle timer when hiding via scroll
        clearTimeout(idleTimer);
        resetIdleTimer(); // Restart idle timer so banner can show again after inactivity
      } else if (scrollState.direction === 'up' && !bannerIsVisible && !shouldHideNearFooter()) {
        showMobileBanner();
        // Clear idle timer when showing via scroll to prevent conflicts
        clearTimeout(idleTimer);
      }
    }

    isScrolling = true;

    // Set scrolling flag to false after a delay
    setTimeout(() => {
      isScrolling = false;
    }, 150);
  }

  // Reset idle timer
  function resetIdleTimer() {
    clearTimeout(idleTimer);
    clearTimeout(idleResetTimeout);

    // Debounce idle timer reset to prevent rapid firing
    idleResetTimeout = setTimeout(() => {
      // Only start idle timer if banner is not visible and should show
      if (!bannerIsVisible && shouldShowBanner()) {
        idleTimer = setTimeout(() => {
          if (!isScrolling) {
            showMobileBanner();
          }
        }, IDLE_DELAY);
      }
    }, 100);
  }

  // Setup idle detection
  function setupIdleDetection() {
    // Events that reset idle timer
    const events = ['mousedown', 'mousemove', 'keypress', 'touchstart', 'touchmove'];

    events.forEach(event => {
      document.addEventListener(event, () => resetIdleTimer(), { passive: true });
    });

    // Start idle timer
    resetIdleTimer();
  }

  // Handle form trigger clicks
  function handleFormTrigger(e, trigger) {
    e.preventDefault();

    // DEF-001: Prevent multiple clicks while form is open
    if (isFormOpen) {
      return;
    }

    const formId = trigger.getAttribute('data-klaviyo-form-id');
    if (!formId) return;

    // Set flag to prevent multiple opens
    isFormOpen = true;

    // Try to open Klaviyo form
    if (window._klOnsite && window._klOnsite.push) {
      window._klOnsite.push(['openForm', formId]);

      // Failsafe: Reset flag after 3 seconds ONLY if form fails to open
      // If form opens successfully, the 'open' event will clear this timeout
      clearTimeout(formOpenTimeout);
      formOpenTimeout = setTimeout(() => {
        isFormOpen = false;
      }, 3000);
    } else {
      // Fallback if Klaviyo not available
      window.location.href = config.fallbackUrl;
      // Reset flag immediately for fallback
      isFormOpen = false;
    }
  }

  // Show CTAs based on current dismissal state
  function showCTAsBasedOnState() {
    const dismissalData = getStorageItem('klaviyoCTADismissal');

    if (dismissalData) {
      try {
        const data = JSON.parse(dismissalData);

        if (data.reason === 'signup') {
          // User signed up - don't show any CTAs
          return;
        }

        if (data.reason === 'manual_close' && !isCTADismissalExpired()) {
          // Show header CTA only
          showHeaderCTA();
        } else {
          // Dismissal expired - show floating CTAs
          showFloatingCTAs();
        }
      } catch (e) {
        // Can't parse - show floating CTAs
        showFloatingCTAs();
      }
    } else {
      // No dismissal - show floating CTAs
      showFloatingCTAs();
    }
  }

  // Setup Klaviyo form event listeners
  function setupKlaviyoListeners() {
    window.addEventListener('klaviyoForms', function (e) {
      // Handle form open event
      if (e.detail.type === 'open') {
        clearTimeout(formOpenTimeout);
        // Hide floating CTAs while form is open
        hideFloatingCTAs();
      }

      // Handle form close event
      if (e.detail.type === 'close') {
        isFormOpen = false;
        clearTimeout(formOpenTimeout);
        // Restore CTAs based on current state
        showCTAsBasedOnState();
      }

      // Handle form submit event - hide all CTAs forever
      if (e.detail.type === 'submit') {
        handleSignup();
      }
    });
  }

  // Initialize desktop functionality
  function initDesktop() {
    const teaser = document.getElementById('desktop-floating-teaser');
    if (!teaser) return;

    // Close button handler
    const closeBtn = document.getElementById('teaser-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        handleFloatingDismissal();
      });
    }
  }

  // Remove duplicate function definitions - they're already defined above

  // Initialize mobile banner after popup has been shown
  function initMobileBannerAfterPopup() {
    // Listen to unified scroll events instead of direct scroll
    window.addEventListener('unifiedScroll', handleScroll);

    // Setup idle detection
    setupIdleDetection();
  }

  // Initialize mobile functionality
  function initMobile() {
    const banner = document.getElementById('mobile-sticky-banner');
    if (!banner) return;

    // Close button handler
    const closeBtn = document.getElementById('banner-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        clearTimeout(idleTimer);
        hideMobileBanner('manual_close');
      });
    }

    // Initialize scroll/idle functionality
    if (window.innerWidth < 1024) {
      initMobileBannerAfterPopup();
    }
  }

  // Initialize everything
  function init() {
    // Initialize unified scroll manager for all devices
    if (window.unifiedScrollManager && !window.unifiedScrollManager.isInitialized) {
      window.unifiedScrollManager.init();
    }

    // Sync storage
    syncStorageOnLoad();

    // Setup event listeners
    setupKlaviyoListeners();

    // Setup click handlers for all triggers
    document.addEventListener('click', function (e) {
      const trigger = e.target.closest('.klaviyo_form_trigger');
      if (trigger) {
        handleFormTrigger(e, trigger);
      }
    });

    // Initialize platform features
    initDesktop();
    initMobile();

    // Show CTAs based on initial state
    showCTAsBasedOnState();
  }

  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
